import styled, { css } from 'styled-components';
import { ifProp, palette, prop, theme, withProp } from 'styled-tools';

import { maxWidth, minWidth } from '../../utils/mediaQueries';
import { fontFamily, fontSize, FontSizes, fontWeight, FontWeights } from '../../utils/typography';
import { CalmTheme, PaletteColor, PortalTheme } from '../ThemeProvider';

export const Wrapper = styled.div<{
	isEmpty: boolean;
	$noMargin: boolean;
}>`
	align-self: baseline;
	display: flex;
	flex-wrap: wrap;
	position: relative;
	width: 100%;

	${p =>
		!p.$noMargin &&
		css`
			margin-bottom: 8px;

			${minWidth('tablet')} {
				margin-bottom: 16px;
			}
		`}
`;

export const BackgroundWrapper = styled.div<{
	$backgroundColor?: string;
	$disabled?: boolean;
	$borderRadius?: string;
}>`
	background-color: ${props =>
		palette(props.$disabled ? 'inputBgDisabled' : props.$backgroundColor ?? 'white')(props)};
	border-radius: ${p => p.$borderRadius || theme('inputs.borderRadius')};
	display: flex;
	height: 100%;
	position: relative;
	width: 100%;
`;

export interface DefaultCustomInputProps {
	$UNSAFE_showFocus?: boolean;
	$hasIcon: boolean;
	$hidePlaceholder: boolean;
	$isEmpty: boolean;
	$isError?: boolean;
	$isValid?: boolean;
	$validationShown?: boolean;
	disabled?: boolean;
	required?: boolean;
	$textColor?: string;
	$provideSpaceForAnimatedLabel: boolean;
	$borderRadius?: string;
	$borderColor?: PaletteColor;
	$isCenteredText?: boolean;
	$isLargerInput?: boolean;
	$isTypeDate?: boolean;
}

const borderWidth = (
	props: DefaultCustomInputProps & {
		theme: typeof CalmTheme | typeof PortalTheme;
	},
	isFocus: boolean,
): string => {
	if (props.$isValid) {
		return props.theme.inputs.borderWidthSuccess;
	}
	if (props.$isError) {
		return props.theme.inputs.borderWidthError;
	}
	if (isFocus) {
		return props.theme.inputs.borderWidthFocus;
	}
	return props.theme.inputs.borderWidth;
};

const focusStyles = css<DefaultCustomInputProps>`
	border: 1px solid ${palette('inputBorderFocus')};
	outline: none;

	border-color: ${withProp(['$isError', '$isValid'], (isError, isValid) => {
		if (isValid) {
			return palette('inputBorderSuccess');
		}
		if (isError) {
			return palette('inputBorderError');
		}
		return palette('inputBorderFocus');
	})};
	border-width: ${props => borderWidth(props, true)};

	padding-right: ${props =>
		`calc(${
			props.$validationShown && props.theme.inputs.showValidationImage ? '45px' : '13px'
		} - ${borderWidth(props, true)})`};
	padding-left: ${props => `calc(${props.$hasIcon ? '45px' : '13px'} - ${borderWidth(props, true)})`};
	${minWidth('mobileLarge')} {
		padding-right: ${props =>
			`calc(${
				props.$validationShown && props.theme.inputs.showValidationImage ? '53px' : '13px'
			} - ${borderWidth(props, true)})`};
		padding-left: ${props => `calc(${props.$hasIcon ? '53px' : '13px'} - ${borderWidth(props, true)})`};
	}
`;

export const Input = styled.input<DefaultCustomInputProps>`
	border-radius: ${prop('$borderRadius', theme('inputs.borderRadius'))};
	border: ${theme('inputs.borderWidth')} solid ${palette('inputBorderFull')};
	border-color: ${props => {
		if (props.$borderColor) {
			return palette(props.$borderColor);
		}
		if (props.$isValid) {
			return palette('inputBorderSuccess');
		}
		if (props.$isError) {
			return palette('inputBorderError');
		}
		if (props.$isEmpty) {
			return palette('inputBorderEmpty');
		}
		return palette('inputBorderFull');
	}};
	border-width: ${props => borderWidth(props, false)};
	color: ${props => (props.$textColor ? palette(props.$textColor) : palette('inputText'))};
	display: inline-block;
	${fontFamily};
	${fontWeight(FontWeights.Medium)};
	${ifProp('$isLargerInput', fontSize(FontSizes.xl), fontSize(FontSizes.base))};
	height: ${ifProp('$isLargerInput', '56px', '3em')};
	padding-top: ${p => (p.$provideSpaceForAnimatedLabel ? '16px' : '0')};
	padding-right: ${props =>
		`calc(${
			props.$validationShown && props.theme.inputs.showValidationImage ? '45px' : '13px'
		} - ${borderWidth(props, false)})`};
	padding-bottom: 0;
	padding-left: ${props => `calc(${props.$hasIcon ? '45px' : '13px'} - ${borderWidth(props, false)})`};
	${minWidth('mobileLarge')} {
		height: ${ifProp('$isLargerInput', '56px', `${28 / 9}em`)};
		padding-top: ${p => (p.$provideSpaceForAnimatedLabel ? '19px' : '0')};
		padding-right: ${props =>
			`calc(${
				props.$validationShown && props.theme.inputs.showValidationImage ? '53px' : '13px'
			} - ${borderWidth(props, false)})`};
		padding-bottom: 0;
		padding-left: ${props => `calc(${props.$hasIcon ? '53px' : '13px'} - ${borderWidth(props, false)})`};
	}
	${maxWidth('mobileLarge')} {
		${ifProp('$isLargerInput', fontSize(FontSizes['3xl']), fontSize(FontSizes.base))};
		height: ${ifProp('$isLargerInput', '82px', '3em')};
	}
	text-align: ${ifProp('$isCenteredText', 'center', 'left')};
	width: 100%;
	background-color: transparent;
	${ifProp(
		'$isTypeDate',
		css`
			@supports (-webkit-backdrop-filter: none) {
				padding-top: ${ifProp('$provideSpaceForAnimatedLabel', '16px', '12px')};
				padding-bottom: ${ifProp('$provideSpaceForAnimatedLabel', '0px', '12px')};
				${minWidth('mobileLarge')} {
					padding-top: ${ifProp('$provideSpaceForAnimatedLabel', '19px', '12px')};
				}
			}
		`,
	)}

	&::placeholder {
		color: ${p => (p.disabled ? palette('inputPlaceholderDisabled') : palette('inputPlaceholder'))};
		opacity: ${p => (p.$hidePlaceholder ? 0 : 1)};
	}

	&:focus {
		${focusStyles}
	}
	${ifProp('$UNSAFE_showFocus', focusStyles)}
`;

export const IconWrapper = styled.span<{ iconColor?: PaletteColor }>`
	width: 1.5rem;
	height: 1.5rem;
	position: absolute;
	left: 14px;
	top: 50%;
	transform: translateY(-50%);

	svg {
		width: 1.5rem;
		height: 1.5rem;

		path {
			width: 100%;
			height: 100%;
			fill: ${p => (p.iconColor ? palette(p.iconColor) : palette('inputIcon'))};
		}
	}
`;

export const Label = styled.label<{
	$isDisabled?: boolean;
	$isFocusedOrHasValue: boolean;
	$hasIcon: boolean;
	$textColor?: string;
	$labelFocusScaleTransform?: number;
}>`
	color: ${p =>
		// eslint-disable-next-line no-nested-ternary
		p.$isDisabled
			? palette('inputPlaceholderDisabled')
			: p.$textColor
			? palette(p.$textColor)
			: p.$isFocusedOrHasValue
			? palette('inputLabel')
			: palette('inputPlaceholder')};
	${fontSize(FontSizes.base)};
	${fontWeight(FontWeights.Medium)};
	left: 0;
	pointer-events: none;
	position: absolute;
	top: 0;
	transform: ${p => {
		// +1 for the border
		const x = (p.$hasIcon ? 44 : 12) + 1;
		const y = (48 - 24) / 2; // (height - lineHeight) / 2
		const scaleTransform = p.$labelFocusScaleTransform ?? 0.66;
		return p.$isFocusedOrHasValue
			? `translate3d(${x}px, 5px, 0) scale(${scaleTransform})`
			: `translate3d(${x}px, ${y}px, 0)`;
	}};
	${minWidth('mobileLarge')} {
		transform: ${p => {
			// +1 for the border
			const x = (p.$hasIcon ? 52 : 12) + 1;
			const y = (56 - 28) / 2; // (height - lineHeight) / 2
			const scaleTransform = p.$labelFocusScaleTransform ?? 0.66;
			return p.$isFocusedOrHasValue
				? `translate3d(${x}px, 8px, 0) scale(${scaleTransform})`
				: `translate3d(${x}px, ${y + 2}px, 0)`;
		}};
	}
	transform-origin: 0 0;
	transition: all 0.15s ease-out;
	z-index: 1;
`;

export const ValidationImage = styled.span<{
	isValid?: boolean;
	isVisible?: boolean;
}>`
	display: ${p => (p.isVisible && p.theme.inputs.showValidationImage ? 'flex' : 'none')};
	opacity: ${p => (p.theme.inputs.showValidationImage ? '1' : '0')};
	width: 2rem;
	height: 2rem;
	position: absolute;
	right: 0.7rem;
	top: 1.75rem;
	transform: translateY(-50%);
	svg {
		width: 2rem;
		height: 2rem;
		path {
			fill: ${({ isValid }) =>
				isValid ? palette('inputIconValidationSuccess') : palette('inputIconValidationError')};
		}
	}
`;

const onDarkBackgroundStyles = css`
	border-radius: 4px;
	margin-top: 4px;
`;

export const ErrorContainer = styled.div<{
	$hasDarkBackground?: boolean;
	$isCenteredText?: boolean;
}>`
	${p => fontSize(p.theme.inputs.errorMessageFontSize)}
	${p => fontWeight(p.theme.inputs.errorMessageFontWeight)}
	text-align: ${ifProp('$isCenteredText', 'center', 'left')};
	width: ${ifProp('$isCenteredText', '100%')};
	background-color: ${ifProp('$hasDarkBackground', palette('errorBackground'))};
	color: ${ifProp('$hasDarkBackground', palette('white'), palette('inputLabelError'))};
	padding: ${ifProp('$hasDarkBackground', '4px 8px', '4px 1px 0 0')};
	${ifProp('$hasDarkBackground', onDarkBackgroundStyles)};
`;
